import { type Periodo, formatarDuracao } from '@projetive-op/domain'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { BarraTitulo } from '@/components/cadastros/base'
import { SeletorPeriodo, referenciaDe } from '@/components/gestao/seletor-periodo'
import { SemaforoBadge } from '@/components/ui/semaforo-badge'
import { Cabecalho, Carregando, Linha, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { hojeIso, pct, pctTitulo } from '@/lib/formato'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/indicadores')({ component: Indicadores })

const ROTULO_TIPO: Record<string, string> = {
  PESSOAL: 'Pessoal',
  TECNICA: 'Técnica',
  FALTA_MATERIAL: 'Falta de material',
  MANUTENCAO: 'Manutenção',
  OUTRO: 'Outro',
}

/**
 * Consultas do módulo "Painel e indicadores": tempo parado por motivo (RF-I05)
 * e aproveitamento por operador (RF-I04). Só gestão e diretoria veem nomes.
 */
function Indicadores() {
  const trpc = useTRPC()
  const [periodo, setPeriodo] = useState<Periodo>('dia')
  const [data, setData] = useState(hojeIso)
  const entrada = { periodo, referencia: referenciaDe(data) }

  const parado = useQuery(trpc.painel.tempoParadoPorMotivo.queryOptions(entrada))
  const aproveitamento = useQuery(trpc.painel.aproveitamentoPorOperador.queryOptions(entrada))

  return (
    <>
      <BarraTitulo
        titulo="Indicadores"
        descricao="Tempo parado por motivo e aproveitamento por operador. Dados individuais ficam só aqui — nunca no painel da TV."
      />
      <SeletorPeriodo
        periodo={periodo}
        onPeriodo={(p) => setPeriodo(p ?? 'dia')}
        data={data}
        onData={setData}
        intervalo={parado.data?.periodo}
      />

      {/* `min-w-0`: a tabela larga rola dentro do cartão em vez de empurrar a página. */}
      <div className="grid gap-8 xl:grid-cols-[1fr_1.4fr]">
        <section className="min-w-0">
          <h2 className="mb-3 text-lg font-semibold">
            Tempo parado por motivo
            {parado.data && (
              <span className="ml-2 text-sm font-normal text-fg-muted tabular">
                total {formatarDuracao(parado.data.totalSeg)}
              </span>
            )}
          </h2>
          <Tabela>
            <Cabecalho>
              <Th>Motivo</Th>
              <Th>Tipo</Th>
              <Th className="text-right">Ocorrências</Th>
              <Th className="text-right">Tempo</Th>
            </Cabecalho>
            <tbody>
              {parado.data?.motivos.map((m) => (
                <Linha key={m.motivo.id}>
                  <Td className="font-medium">
                    {m.motivo.nome}
                    {m.motivo.contaComoProdutivo && (
                      <span className="ml-2 text-xs text-fg-subtle">conta como produtivo</span>
                    )}
                  </Td>
                  <Td className="text-fg-muted">{ROTULO_TIPO[m.motivo.tipo] ?? m.motivo.tipo}</Td>
                  <Td numerico className="text-right">
                    {m.ocorrencias}
                  </Td>
                  <Td numerico className="text-right font-semibold">
                    {formatarDuracao(m.segundos)}
                  </Td>
                </Linha>
              ))}
              {parado.isPending && <Carregando colunas={4} />}
              {parado.data?.motivos.length === 0 && (
                <Vazio colunas={4}>Nenhuma pausa no período.</Vazio>
              )}
            </tbody>
          </Tabela>
        </section>

        <section className="min-w-0">
          <h2 className="mb-3 text-lg font-semibold">Aproveitamento por operador</h2>
          <Tabela>
            <Cabecalho>
              <Th>Operador</Th>
              <Th className="text-right">Fichas</Th>
              <Th className="text-right">Logado</Th>
              <Th className="text-right">Produzindo</Th>
              <Th className="text-right">Eficiência</Th>
              <Th className="text-right">Produtividade</Th>
              <Th className="text-right">Aproveitamento</Th>
              <Th>Ritmo</Th>
            </Cabecalho>
            <tbody>
              {aproveitamento.data?.operadores.map((o) => (
                <Linha key={o.operador.id}>
                  <Td nowrap>
                    <span className="font-medium">{o.operador.nome}</span>
                    {o.operador.turno && (
                      <span className="block text-xs text-fg-subtle">{o.operador.turno}</span>
                    )}
                  </Td>
                  <Td numerico className="text-right">
                    {o.fichasConcluidas}
                  </Td>
                  <Td numerico className="text-right">
                    {formatarDuracao(o.tempoLogadoSeg)}
                    {o.tempoAlemDoTurnoSeg > 0 && (
                      <span className="block text-xs text-fg-subtle">
                        {formatarDuracao(o.tempoAlemDoTurnoSeg)} de hora extra
                      </span>
                    )}
                  </Td>
                  <Td numerico className="text-right">
                    {formatarDuracao(o.tempoProdutivoSeg)}
                    {o.tempoAjudaSeg > 0 && (
                      <span className="block text-xs text-fg-subtle">
                        {formatarDuracao(o.tempoAjudaSeg)} ajudando
                      </span>
                    )}
                    {o.ajudaNaoConfirmadaSeg > 0 && (
                      <span
                        className="block text-xs text-amarelo"
                        title='Pausa "ajudando outro posto" sem o PIN do ajudante no outro tablet: não conta como produtiva.'
                      >
                        {formatarDuracao(o.ajudaNaoConfirmadaSeg)} ajuda sem confirmação
                      </span>
                    )}
                  </Td>
                  <Td numerico className="text-right" title={pctTitulo(o.eficiencia)}>
                    {pct(o.eficiencia)}
                  </Td>
                  <Td numerico className="text-right">
                    {pct(o.produtividade)}
                  </Td>
                  <Td
                    numerico
                    className="text-right font-semibold"
                    title={pctTitulo(o.aproveitamento)}
                  >
                    {pct(o.aproveitamento)}
                  </Td>
                  <Td>
                    <SemaforoBadge cor={o.semaforo} />
                  </Td>
                </Linha>
              ))}
              {aproveitamento.isPending && <Carregando colunas={8} />}
              {aproveitamento.data?.operadores.length === 0 && (
                <Vazio colunas={8}>Nenhum operador ativo.</Vazio>
              )}
            </tbody>
          </Tabela>
        </section>
      </div>
    </>
  )
}
